import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Phone, PhoneCall, MessageSquare, Clock,
  CheckCircle, XCircle, AlertCircle, PhoneOff,
  Send, RefreshCw, Radio
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DialerPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [activeTab, setActiveTab] = useState("calls");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [calling, setCalling] = useState(false);
  const [sending, setSending] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [smsHistory, setSmsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    fetchHistory();
    fetchTemplates();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const [callsRes, smsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dialer/history/calls?limit=50`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/dialer/history/sms?limit=50`, { withCredentials: true })
      ]);
      setCallHistory(callsRes.data.calls || []);
      setSmsHistory(smsRes.data.messages || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dialer/templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
    } catch {}
  };

  const handleCall = async () => {
    if (!phoneNumber) { toast.error("Enter a phone number"); return; }
    setCalling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/dialer/call`, { to_number: phoneNumber, contact_name: "Manual Dial" }, { withCredentials: true });
      if (res.data.demo_mode) toast.info("Demo Mode — connect Twilio for live calls");
      else if (res.data.success) toast.success(`Calling ${phoneNumber}...`);
      else toast.error(res.data.error || "Failed to initiate call");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to make call");
    } finally { setCalling(false); }
  };

  const handleSendSMS = async () => {
    if (!phoneNumber || !message.trim()) { toast.error("Enter a phone number and message"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/dialer/sms`, { to_number: phoneNumber, message, contact_name: "Manual SMS" }, { withCredentials: true });
      if (res.data.demo_mode) toast.info("Demo Mode — connect Twilio for live messages");
      else if (res.data.success) toast.success("SMS sent!");
      else toast.error(res.data.error || "Failed to send");
      setMessage("");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send SMS");
    } finally { setSending(false); }
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplate(templateId);
    if (!templateId || templateId === "none") { setMessage(""); return; }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/dialer/templates/render`, { template_id: templateId }, { withCredentials: true });
      setMessage(res.data.rendered_message || "");
    } catch { toast.error("Failed to load template"); }
  };

  const formatPhone = (value) => {
    const d = value.replace(/\D/g, "");
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  const StatusIcon = ({ status }) => {
    if (status === "completed" || status === "delivered") return <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
    if (status === "no-answer" || status === "busy") return <PhoneOff className="w-4 h-4 text-amber-400 flex-shrink-0" />;
    if (status === "failed") return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
    if (status === "demo") return <AlertCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />;
    return <Clock className="w-4 h-4 text-white/40 flex-shrink-0" />;
  };

  const fmtDuration = (s) => { if (!s) return "—"; return `${Math.floor(s/60)}:${(s%60).toString().padStart(2,"0")}`; };
  const fmtTime = (t) => { if (!t) return "—"; return new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }); };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dialer-page">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />

      <main className={mainClass}>
        {/* Header */}
        <header className="border-b border-white/[0.06] bg-black/80 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-white">Dialer</h1>
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Call & SMS Outreach</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
              <Radio className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Live</span>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6">
          <div className="grid lg:grid-cols-3 gap-6">

            {/* Left — Dial Pad */}
            <div className="lg:col-span-1 space-y-4">
              {/* Quick Dial Card */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <PhoneCall className="w-4 h-4 text-[#D4AF37]" />
                  <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Quick Dial</h2>
                </div>

                <div className="mb-4">
                  <Label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Phone Number</Label>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhone(e.target.value))}
                    placeholder="(555) 123-4567"
                    className="bg-black/60 border-white/[0.08] text-white text-lg h-12 font-mono placeholder:text-white/20 focus:border-[#D4AF37]/40"
                    data-testid="phone-input"
                  />
                </div>

                <Button
                  onClick={handleCall}
                  disabled={!phoneNumber || calling}
                  className="w-full h-12 bg-[#D4AF37] hover:bg-[#C9A227] text-black font-bold text-base mb-1"
                  data-testid="call-btn"
                >
                  {calling ? (
                    <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Calling...</>
                  ) : (
                    <><Phone className="w-4 h-4 mr-2" /> Call Now</>
                  )}
                </Button>
              </div>

              {/* SMS Card */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
                  <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Send SMS</h2>
                </div>

                {templates.length > 0 && (
                  <div className="mb-3">
                    <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                      <SelectTrigger className="bg-black/60 border-white/[0.08] text-white">
                        <SelectValue placeholder="Use a template..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/[0.08]">
                        <SelectItem value="none" className="text-white/60">No template</SelectItem>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="bg-black/60 border-white/[0.08] text-white mb-3 min-h-[100px] resize-none placeholder:text-white/20 focus:border-[#D4AF37]/40"
                  data-testid="sms-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/30 font-mono">{message.length}/160</span>
                  <Button
                    onClick={handleSendSMS}
                    disabled={!phoneNumber || !message.trim() || sending}
                    className="bg-[#D4AF37] hover:bg-[#C9A227] text-black font-bold h-9 px-4"
                    data-testid="send-sms-btn"
                  >
                    {sending ? "Sending..." : <><Send className="w-4 h-4 mr-1.5" /> Send</>}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right — History */}
            <div className="lg:col-span-2">
              {/* Tab bar */}
              <div className="flex items-center gap-1 mb-4 border-b border-white/[0.06]">
                {[
                  { key: "calls", icon: Phone, label: "Call History" },
                  { key: "sms", icon: MessageSquare, label: "SMS History" },
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
                      activeTab === key
                        ? "border-[#D4AF37] text-[#D4AF37]"
                        : "border-transparent text-white/40 hover:text-white/70"
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
                <button
                  onClick={fetchHistory}
                  className="ml-auto p-2 text-white/30 hover:text-white transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingHistory ? "animate-spin" : ""}`} />
                </button>
              </div>

              {/* History list */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl overflow-hidden">
                {loadingHistory ? (
                  <div className="flex items-center justify-center p-12">
                    <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : activeTab === "calls" ? (
                  callHistory.length === 0 ? (
                    <div className="p-12 text-center">
                      <Phone className="w-12 h-12 mx-auto mb-3 text-white/10" />
                      <p className="text-white/40 text-sm">No call history yet</p>
                      <p className="text-white/20 text-xs mt-1">Make your first call to see it here</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/[0.06]">
                      {callHistory.map((call, idx) => (
                        <div key={idx} className="p-4 hover:bg-white/[0.02] transition-colors">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <StatusIcon status={call.status} />
                              <div>
                                <p className="text-white text-sm font-medium">{call.contact_name || call.to_number}</p>
                                <p className="text-white/40 text-xs font-mono">{call.to_number}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-white/60 text-sm font-mono">{fmtDuration(call.duration)}</p>
                              <p className="text-white/30 text-xs">{fmtTime(call.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  smsHistory.length === 0 ? (
                    <div className="p-12 text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-white/10" />
                      <p className="text-white/40 text-sm">No SMS history yet</p>
                      <p className="text-white/20 text-xs mt-1">Send your first message to see it here</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/[0.06]">
                      {smsHistory.map((sms, idx) => (
                        <div key={idx} className="p-4 hover:bg-white/[0.02] transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <StatusIcon status={sms.status} />
                              <div>
                                <p className="text-white text-sm font-medium">{sms.contact_name || sms.to_number}</p>
                                <p className="text-white/50 text-xs mt-0.5 line-clamp-2">{sms.message}</p>
                              </div>
                            </div>
                            <p className="text-white/30 text-xs whitespace-nowrap flex-shrink-0">{fmtTime(sms.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
